import { useEffect, useState } from "react";
import { Key, Plus, Trash, UserGear } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";

const ROLE_OPTIONS = [
    { value: "coach", label: "Pelatih" },
    { value: "finance", label: "Admin Keuangan" },
];

export default function StaffAccountsTab() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState({ username: "", password: "", role: "coach" });
    const [passwords, setPasswords] = useState({});
    const [lastPassword, setLastPassword] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/staff-accounts");
            setItems(data);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    useEffect(() => {
        load();
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post("/admin/staff-accounts", form);
            toast.success("Akun staff dibuat");
            setForm({ username: "", password: "", role: "coach" });
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const resetPassword = async (account) => {
        try {
            const { data } = await api.post(`/admin/staff-accounts/${account.id}/password`, {
                new_password: passwords[account.id] || "",
            });
            setLastPassword(data);
            toast.success("Password staff diperbarui");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    const remove = async (account) => {
        if (!window.confirm(`Hapus akun ${account.username}?`)) return;
        try {
            await api.delete(`/admin/staff-accounts/${account.id}`);
            toast.success("Akun staff dihapus");
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    return (
        <div className="space-y-8" data-testid="staff-accounts-tab">
            <section className="bg-white border border-racing-line p-6">
                <h2 className="font-display uppercase text-2xl mb-2">Tambah Akun Staff</h2>
                <p className="text-sm text-racing-muted mb-6">Admin cukup mengisi username, password, dan role akun.</p>
                <form onSubmit={submit} className="grid md:grid-cols-4 gap-4" data-testid="staff-account-form">
                    <input required minLength={3} value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="staff-username-input" className="bg-racing-surface border border-racing-line px-4 py-3 text-sm" placeholder="Username" />
                    <input required minLength={6} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-password-input" className="bg-racing-surface border border-racing-line px-4 py-3 text-sm" placeholder="Password" />
                    <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="staff-role-select" className="bg-racing-surface border border-racing-line px-4 py-3 text-sm">
                        {ROLE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                    <button disabled={saving} data-testid="staff-create-button" className="flex items-center justify-center gap-2 bg-racing-red text-white px-5 py-3 text-xs uppercase tracking-[0.2em]"><Plus size={16} /> {saving ? "Menyimpan..." : "Tambah"}</button>
                </form>
            </section>

            {lastPassword && (
                <div className="border border-racing-gold bg-racing-gold/10 p-5" data-testid="staff-password-result">
                    Password baru untuk <strong>{lastPassword.username}</strong>: <strong className="text-racing-red">{lastPassword.new_password}</strong>
                </div>
            )}

            <section className="border border-racing-line overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]">
                    <thead>
                        <tr className="border-b border-racing-line bg-racing-surface">
                            {["Username", "Role", "Dibuat", "Password Baru", "Aksi"].map((h) => <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-racing-muted">{h}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && <tr><td colSpan="5" className="px-4 py-8 text-center text-racing-muted" data-testid="staff-empty">Belum ada akun pelatih/admin keuangan.</td></tr>}
                        {items.map((account, index) => (
                            <tr key={account.id} className="border-b border-racing-line last:border-0" data-testid={`staff-row-${index}`}>
                                <td className="px-4 py-3 font-medium"><UserGear size={16} className="inline mr-2 text-racing-red" />{account.username}</td>
                                <td className="px-4 py-3 capitalize">{account.role === "coach" ? "Pelatih" : "Admin Keuangan"}</td>
                                <td className="px-4 py-3 text-xs text-racing-muted">{(account.created_at || "").slice(0, 10)}</td>
                                <td className="px-4 py-3"><input value={passwords[account.id] || ""} onChange={(e) => setPasswords({ ...passwords, [account.id]: e.target.value })} data-testid={`staff-new-password-${index}`} className="w-40 bg-racing-surface border border-racing-line px-3 py-2 text-xs" placeholder="Kosongkan utk otomatis" /></td>
                                <td className="px-4 py-3">
                                    <div className="flex gap-2">
                                        <button onClick={() => resetPassword(account)} data-testid={`staff-reset-${index}`} className="p-2 border border-racing-line text-racing-muted hover:text-racing-red"><Key size={15} /></button>
                                        <button onClick={() => remove(account)} data-testid={`staff-delete-${index}`} className="p-2 border border-racing-line text-racing-red hover:bg-racing-red hover:text-white"><Trash size={15} /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>
        </div>
    );
}
